import { useEffect, useState } from 'react';
import api from '../services/api';

interface AuditUser { id: string; email: string; firstName: string; lastName: string; }
interface AuditEvent { id: string; timestamp: string; user?: AuditUser | null; eventType: string; details?: any; }

function Audit() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);

  const loadAudit = async (p = page, l = limit) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(l) });
      const res = await api.get(`/audit?${params.toString()}`);
      setEvents(res.data.data || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || p);
      setLimit(res.data.limit || l);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load audit logs');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAudit(1, limit); }, []);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const toPlainText = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
  };

  const formatDetails = (details: any): { short: string; full: string } => {
    if (!details) return { short: '—', full: '' };
    try {
      // If it's a JSON string, parse first so we can extract friendly fields
      let parsed: any = details;
      if (typeof details === 'string') {
        const trimmed = details.trim();
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
          try { parsed = JSON.parse(trimmed); } catch { /* leave as string */ }
        }
      }
      const full = typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2);

      if (typeof parsed === 'string') {
        return { short: parsed, full };
      }

      // If a top-level message exists, prefer showing only its text
      if (parsed && typeof parsed === 'object' && Object.prototype.hasOwnProperty.call(parsed, 'message')) {
        const msg = toPlainText(parsed.message);
        if (msg) return { short: msg, full };
      }

      // Prefer common fields if present
      const candidates: Array<[string, any]> = [];
      const pushIf = (k: string) => {
        if (parsed && Object.prototype.hasOwnProperty.call(parsed, k)) {
          candidates.push([k, parsed[k]]);
        }
      };
      ['message', 'status', 'action', 'entityType', 'entityId', 'count', 'user', 'ipAddress'].forEach(pushIf);

      // If a nested 'changes' object exists, summarize it
      if (parsed && typeof parsed.changes === 'object' && parsed.changes) {
        const changedFields = Object.keys(parsed.changes).slice(0, 5).join(', ');
        if (changedFields) candidates.push(['changes', changedFields + (Object.keys(parsed.changes).length > 5 ? '…' : '')]);
      }

      let short = '';
      if (candidates.length > 0) {
        short = candidates
          .map(([k, v]) => `${k}: ${toPlainText(v) || (typeof v === 'object' ? '[object]' : '')}`)
          .filter(Boolean)
          .join(' • ');
      } else {
        // Fallback: flatten top-level up to 5 keys
        const keys = Object.keys(parsed).slice(0, 5);
        short = keys
          .map(k => `${k}: ${toPlainText(parsed[k]) || (typeof parsed[k] === 'object' ? '[object]' : '')}`)
          .join(' • ');
      }

      if (short.length > 140) short = short.slice(0, 137) + '…';
      return { short: short || '[details]', full };
    } catch {
      const s = typeof details === 'string' ? details : '[details]';
      return { short: s, full: s };
    }
  };

  return (
    <div>
      <h1>Audit Trail</h1>
      <div style={{ marginTop: '20px' }}>
        <p>View all system activity logs</p>
      </div>
      <div style={{ marginTop: '30px' }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px' }}>
            <div style={{ color: '#6c757d', fontSize: '14px' }}>Total: {total}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ fontSize: '14px', color: 'var(--secondary-color)' }}>Rows:</label>
              <select value={limit} onChange={(e) => { const v = parseInt(e.target.value); setLimit(v); loadAudit(1, v); }} style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '8px', overflow: 'hidden', boxShadow: 'var(--elevation-1)' }}>
            <thead>
              <tr style={{ backgroundColor: 'rgba(1, 95, 147, 0.05)', borderBottom: '2px solid rgba(1, 95, 147, 0.1)' }}>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--secondary-color)' }}>Timestamp</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--secondary-color)' }}>User</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--secondary-color)' }}>Event</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: '600', color: 'var(--secondary-color)' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} style={{ padding: '40px', textAlign: 'center', color: 'var(--secondary-color)' }}>Loading...</td></tr>
              ) : error ? (
                <tr><td colSpan={4} style={{ padding: '40px', textAlign: 'center', color: '#dc3545' }}>{error}</td></tr>
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: '40px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                      <span className="material-symbols-outlined large" style={{ color: '#6c757d' }}>history</span>
                      <p style={{ color: '#6c757d', fontStyle: 'italic', margin: 0 }}>No audit events found.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                events.map(ev => (
                  <tr key={ev.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px 8px', color: '#6c757d' }}>{new Date(ev.timestamp).toLocaleString()}</td>
                    <td style={{ padding: '12px 8px' }}>{ev.user ? `${ev.user.firstName} ${ev.user.lastName}` : '—'}</td>
                    <td style={{ padding: '12px 8px' }}>{ev.eventType}</td>
                    <td style={{ padding: '12px 8px', maxWidth: '520px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={formatDetails(ev.details).full}>
                      {formatDetails(ev.details).short}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px' }}>
            <div style={{ color: '#6c757d', fontSize: '14px' }}>Page {page} of {totalPages}</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => { if (page > 1) { const np = page - 1; setPage(np); loadAudit(np, limit); } }} disabled={page <= 1} style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #015F93', color: '#015F93', background: 'none', opacity: page <= 1 ? 0.6 : 1, cursor: page <= 1 ? 'not-allowed' : 'pointer' }}>
                <span className="material-symbols-outlined small">chevron_left</span>
                Previous
              </button>
              <button onClick={() => { if (page < totalPages) { const np = page + 1; setPage(np); loadAudit(np, limit); } }} disabled={page >= totalPages} style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #015F93', color: '#015F93', background: 'none', opacity: page >= totalPages ? 0.6 : 1, cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}>
                Next
                <span className="material-symbols-outlined small">chevron_right</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Audit;


